import type { CSSProperties, ReactElement } from 'react';
import { useState } from 'react';
import styled from 'styled-components';

interface Props {
  /** Optional leading label (e.g. `CID`). Hidden when omitted. */
  label?: string;
  /** Text shown inside the pill. */
  value: string;
  /** What gets copied to the clipboard (defaults to `value`). */
  copyValue?: string;
  className?: string;
  style?: CSSProperties;
}

const Pill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 9px;
  background: rgba(17, 27, 51, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(205, 214, 234, 0.78);
  font-size: 11px;
  /* Mulish (the app default), not the mono face — inherits from body on purpose. */
  transition: all var(--transition);

  i {
    color: rgba(205, 214, 234, 0.78);
    font-size: 11px;
  }

  &:hover,
  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.2);
    background: rgba(17, 27, 51, 0.9);
  }

  &.copied i {
    color: rgb(53, 211, 153);
  }
`;

/*
 * Small copy-to-clipboard pill: an optional label + value, with a copy icon
 * that flips to a green check for ~1.2s after clicking. Manages its own state,
 * so multiple pills are independent.
 */
export const CopyPill = ({ label, value, copyValue, className, style }: Props): ReactElement => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (): void => {
    void navigator.clipboard.writeText(copyValue ?? value);
    setCopied(true);

    setTimeout(() => {
      setCopied(false);
    }, 1200);
  };

  return (
    <Pill
      type='button'
      className={`${copied ? 'copied' : ''}${className ? ` ${className}` : ''}`}
      style={style}
      onClick={handleCopy}
    >
      {label !== undefined && <span className='text'>{label}</span>}
      <span className='value'>{value}</span>
      <i className={copied ? 'fas fa-check' : 'far fa-copy'} />
    </Pill>
  );
};
